import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { baseFetch } from 'src/utils';
import { ToastStyled } from 'src/components';
import type { CartGetData, UserProfile } from 'src/types';

export const useDisassociateCart = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async ({ cartId }: { cartId: string }): Promise<boolean> => {
      const res = await baseFetch(`/user/cart/delete/${cartId}`, {
        method: 'DELETE',
      });

      if (!res.ok) {
        throw new Error('Failed to delete cart');
      }

      const { status } = await res.json();

      // since status is an empty string if successful, we can check
      // to see if status exists, if it does, it means there was an error
      if (status) {
        throw new Error('Error updating cart... status...');
      }

      // return true to trigger the onSuccess below
      return true;
    },
    onSuccess: (mutationReturn, variables) => {
      // update cart list queries to remove deleted cart
      // update cart cartId to remove ownership data
      toast.custom((toastBlock) => (
        <ToastStyled
          variant='success'
          handleClose={() => toast.dismiss(toastBlock)}
        >
          {t('get.cart_removed_from_your_cart_history')}
        </ToastStyled>
      ));
      try {
        queryClient.setQueryData(
          ['currentUser'],
          (oldUserData: UserProfile) => {
            // remove cartId from user's cart list in cache
            const newUserData = { ...oldUserData };

            newUserData.carts = newUserData.carts.filter(
              (cartId) => cartId !== variables.cartId,
            );

            return newUserData;
          },
        );
      } catch (err) {
        queryClient.invalidateQueries({
          queryKey: ['currentUser'],
          type: 'active',
        });
      }

      try {
        queryClient.setQueryData(
          ['cart', variables.cartId],
          (oldData: CartGetData) => {
            // remove ownership data
            const newData = { ...oldData };
            delete newData.ownershipData;
            return newData;
          },
        );
      } catch (err) {
        queryClient.invalidateQueries({
          queryKey: ['cart', variables.cartId],
          type: 'active',
        });
      }

      queryClient.invalidateQueries({
        queryKey: ['cart', 'list'],
        exact: false,
        type: 'all',
      });

      navigate('/plus/cart-history');
    },
    onError: (error) => {
      toast.custom((toastBlock) => (
        <ToastStyled
          variant='error'
          handleClose={() => toast.dismiss(toastBlock)}
        >
          {t('get.something_went_wrong_trying_to_remove_cart_from_history')}
        </ToastStyled>
      ));
    },
  });
};
